import React from "react";
import { GraduationCap, ShieldCheck, Megaphone, LifeBuoy } from "lucide-react";
import BgOrbs from "./BgOrbs";

const CASES = [
  {
    icon: GraduationCap,
    title: "HR onboarding",
    body: "Turn onboarding docs, policies, and welcome decks into guided AI sessions new hires actually complete.",
    outcome: "Turn onboarding docs into guided AI sessions in days, not weeks.",
    tone: "from-blue-500 to-indigo-500",
  },
  {
    icon: ShieldCheck,
    title: "Compliance training",
    body: "Deliver consistent policy training, spot weak spots, and keep your audit trail clean.",
    outcome: "Consistent delivery with topic-level completion visibility.",
    tone: "from-rose-500 to-pink-500",
  },
  {
    icon: Megaphone,
    title: "Sales enablement",
    body: "Convert pitch decks and product docs into interactive rep training that actually sticks.",
    outcome: "Faster rep ramp-up with AI-paced practice sessions.",
    tone: "from-violet-500 to-fuchsia-500",
  },
  {
    icon: LifeBuoy,
    title: "Support readiness",
    body: "Keep support and ops teams sharp with always-on AI tutoring over your SOPs and runbooks.",
    outcome: "Fewer escalations, sharper frontline responses.",
    tone: "from-teal-500 to-emerald-500",
  },
];

export default function UseCases() {
  return (
    <section
      id="use-cases"
      data-testid="use-cases-section"
      className="relative min-h-screen flex items-center py-16 bg-gradient-to-b from-[#eff6ff] via-white to-[#eff6ff] overflow-hidden"
    >
      <BgOrbs variant="violet" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-3xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-700">
            Use cases
          </div>
          <h2 className="mt-3 text-[34px] sm:text-[44px] leading-[1.08] font-extrabold tracking-tight text-slate-900 text-balance">
            Built for every kind of training.
          </h2>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-5">
          {CASES.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                className="card-lift group relative bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl p-7 hover:border-blue-200 hover:shadow-[0_20px_48px_-24px_rgba(37,99,235,0.25)] overflow-hidden"
                data-testid={`use-case-${i}`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${c.tone} text-white shadow-[0_10px_24px_-12px_rgba(37,99,235,0.55)]`}
                  >
                    <Icon size={22} strokeWidth={2} />
                  </div>
                  <h3 className="text-[20px] font-bold tracking-tight text-slate-900">
                    {c.title}
                  </h3>
                </div>
                <p className="mt-4 text-[14.5px] text-slate-600 leading-relaxed">
                  {c.body}
                </p>
                <div className="mt-5 pt-4 border-t border-slate-100 flex items-start gap-2">
                  <span
                    className="mt-1 w-1 h-4 rounded-full"
                    style={{
                      background:
                        "linear-gradient(180deg, #2563eb, #7c3aed)",
                    }}
                  />
                  <span className="text-[13px] font-semibold text-slate-800">
                    {c.outcome}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
